import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Grid, List } from 'lucide-react';
import { novelAPI, genreAPI } from '../../services/api';
import { useGenreStore } from '../../hooks/useStore';
import NovelCard from '../../components/NovelCard/NovelCard';
import AdDisplay from '../../components/AdDisplay/AdDisplay';
import './NovelList.css';

const NovelList = () => {
  const { slug: genreSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [novels, setNovels] = useState([]);
  const [currentGenre, setCurrentGenre] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  
  useGenreStore();
  
  const page = parseInt(searchParams.get('page')) || 1;
  const sort = searchParams.get('sort') || 'newest';
  const status = searchParams.get('status') || '';

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // If viewing a specific genre
        if (genreSlug) {
          const genreRes = await genreAPI.getBySlug(genreSlug);
          setCurrentGenre(genreRes.data);
        } else {
          setCurrentGenre(null);
        }

        // Fetch novels
        const params = {
          page,
          limit: 24,
          sort,
          ...(genreSlug && { genre: genreSlug }),
          ...(status && { status })
        };
        
        const response = await novelAPI.getAll(params);
        setNovels(response.data || []);
        setPagination(response.pagination);
      } catch (error) {
        console.error('Error fetching novels:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    window.scrollTo(0, 0);
  }, [genreSlug, page, sort, status]);

  const handleSortChange = (newSort) => {
    setSearchParams({ sort: newSort, page: '1', ...(status && { status }) });
  };

  const handleStatusChange = (newStatus) => {
    setSearchParams({ sort, page: '1', ...(newStatus && { status: newStatus }) });
  };

  const handlePageChange = (newPage) => {
    setSearchParams({ sort, page: newPage.toString(), ...(status && { status }) });
  };

  const renderPagination = () => {
    if (!pagination || pagination.totalPages <= 1) return null;

    const pages = [];
    const { currentPage, totalPages } = pagination;

    // Previous button
    pages.push(
      <button
        key="prev"
        className="page-btn"
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
      >
        <ChevronLeft size={18} />
      </button>
    );

    // Page numbers
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - 2 && i <= currentPage + 2)
      ) {
        pages.push(
          <button
            key={i}
            className={`page-btn ${i === currentPage ? 'active' : ''}`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        );
      } else if (i === currentPage - 3 || i === currentPage + 3) {
        pages.push(<span key={i} className="page-dots">...</span>);
      }
    }

    // Next button
    pages.push(
      <button
        key="next"
        className="page-btn"
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
      >
        <ChevronRight size={18} />
      </button>
    );

    return <div className="pagination">{pages}</div>;
  };

  return (
    <main className="novel-list-page">
      {/* Ad - Category Top */}
      <AdDisplay position="category_top" className="category-top-ad" />

      <div className="container">
        {/* Page Header */}
        <div className="page-header">
          {currentGenre ? (
            <>
              <div className="genre-badge">
                <span className="genre-icon">{currentGenre.icon}</span>
                <h2>{currentGenre.name}</h2>
              </div>
              <p>{currentGenre.description}</p>
            </>
          ) : (
            <>
              <h1>Novel List</h1>
              <p>Explore our vast collection of novels</p>
            </>
          )}
        </div>

        {/* Filters Bar */}
        <div className="filters-bar">
          <div className="filter-left">
            <div className="filter-group">
              <label>Sort by:</label>
              <select value={sort} onChange={(e) => handleSortChange(e.target.value)}>
                <option value="newest">Latest Updates</option>
                <option value="popular">Most Popular</option>
                <option value="rating">Top Rated</option>
                <option value="chapters">Most Chapters</option>
                <option value="name">Name A-Z</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Status:</label>
              <select value={status} onChange={(e) => handleStatusChange(e.target.value)}>
                <option value="">All</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          <div className="filter-right">
            <div className="view-toggle">
              <button
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
              >
                <Grid size={18} />
              </button>
              <button
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Results Info */}
        {pagination && (
          <div className="results-info">
            Showing {novels.length} of {pagination.total} novels
          </div>
        )}

        {/* Ad - Category Middle */}
        <AdDisplay position="category_middle" className="category-middle-ad" />

        {/* Novels Grid/List */}
        {isLoading ? (
          <div className="loading">
            <div className="loading-spinner"></div>
          </div>
        ) : novels.length > 0 ? (
          <div className={`novels-${viewMode}`}>
            {novels.map((novel) => (
              <NovelCard 
                key={novel._id} 
                novel={novel} 
                variant={viewMode === 'list' ? 'horizontal' : 'default'}
              />
            ))}
          </div>
        ) : (
          <div className="no-results">
            <p>No novels found</p>
          </div>
        )}

        {/* Pagination */}
        {renderPagination()}
      </div>
    </main>
  );
};

export default NovelList;
